import { useState, useEffect } from 'react';
import {
  fetchArticles,
  fetchArticleBySlug,
  fetchArticlesByCategory,
  fetchFeaturedArticles,
  searchArticles
} from '../api/articles/client';

// Hook to fetch all articles
export function useArticles(limit = 20, filterPublished = true) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadArticles = async () => {
      try {
        setLoading(true);
        const data = await fetchArticles(limit, filterPublished);
        setArticles(data);
      } catch (err) {
        setError(err.message);
        console.error('Error loading articles:', err);
      } finally {
        setLoading(false);
      }
    };

    loadArticles();
  }, [limit, filterPublished]);

  return { articles, loading, error };
}

// Hook to fetch single article by slug
export function useArticle(slug) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }

    const loadArticle = async () => {
      try {
        setLoading(true);
        const data = await fetchArticleBySlug(slug);
        setArticle(data);
      } catch (err) {
        setError(err.message);
        console.error('Error loading article:', err);
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [slug]);

  return { article, loading, error };
}

// Hook to fetch articles by category
export function useArticlesByCategory(category, limit = 10) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!category) {
      setLoading(false);
      return;
    }

    const loadArticles = async () => {
      try {
        setLoading(true);
        const data = await fetchArticlesByCategory(category, limit);
        setArticles(data);
      } catch (err) {
        setError(err.message);
        console.error('Error loading articles by category:', err);
      } finally {
        setLoading(false);
      }
    };

    loadArticles();
  }, [category, limit]);

  return { articles, loading, error };
}

// Hook to fetch featured articles
export function useFeaturedArticles(limit = 3) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadArticles = async () => {
      try {
        setLoading(true);
        const data = await fetchFeaturedArticles(limit);
        setArticles(data);
      } catch (err) {
        setError(err.message);
        console.error('Error loading featured articles:', err);
      } finally {
        setLoading(false);
      }
    };

    loadArticles();
  }, [limit]);

  return { articles, loading, error };
}

// Hook to search articles
export function useArticleSearch(query, limit = 10) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!query || query.length < 2) {
      setArticles([]);
      return;
    }

    const delayDebounce = setTimeout(async () => {
      try {
        setLoading(true);
        const data = await searchArticles(query, limit);
        setArticles(data);
      } catch (err) {
        setError(err.message);
        console.error('Error searching articles:', err);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [query, limit]);

  return { articles, loading, error };
}
